import Parser from 'html-react-parser';
import { ReactNode } from 'react';

import { rewriteCosmicImagesInHtml } from './cosmicImgix';
import { sanitizeCosmicHtml } from './htmlSanitize';
import landingToModelsJSON from './landing-to-models.json';
import { replaceLandingRedirects } from './landingRedirects';

// AI generated
// migrates any auto/marken-modelle/{brand}/{model} links to models/{brand}/{model}
export const rewriteMarkenModelleLinks = (input: string) => {
  // Matches:
  //   optional leading slash OR non-word/non-slash boundary (captured as g1),
  //   "auto/marken-modelle/",
  //   brand (g2) and model (g3) segments,
  // while NOT consuming trailing "/" or ?query or #hash (they remain untouched).
  //
  // Why two alternatives for the "prefix"?
  // - (?:^|(?<=\W)) would be ideal, but lookbehinds aren't universally supported.
  // - So we capture either a "/" or a single non-word/non-slash char (or start-of-string via the first branch).
  //
  // The two main branches:
  //  1) (/(...)) — for paths like "/auto/marken-modelle/..."
  //  2) (^|[^A-Za-z0-9/])(...) — for "auto/marken-modelle/..." without a leading slash
  const re = new RegExp(
    [
      // branch 1: starts with a slash
      '(\\/)',
      'auto\\/marken-modelle\\/([^\\/"\'\\s?#]+)\\/([^\\/"\'\\s?#]+)',
      // OR branch 2: start or a non-word non-slash char before "auto/..."
      '|',
      '(^|[^A-Za-z0-9\\/])',
      'auto\\/marken-modelle\\/([^\\/"\'\\s?#]+)\\/([^\\/"\'\\s?#]+)',
    ].join(''),
    'gi'
  );

  return input.replace(
    re,
    (_match, slashPrefix, brand1, model1, nonSlashPrefix, brand2, model2) => {
      // Determine which branch matched
      if (slashPrefix !== undefined) {
        const nextBrand =
          landingToModelsJSON[brand1 as keyof typeof landingToModelsJSON];
        const nextModel =
          landingToModelsJSON[model1 as keyof typeof landingToModelsJSON];
        if (!nextBrand || !nextModel) {
          // No mapping found, return original match
          return _match;
        }

        // Matched "/auto/marken-modelle/{brand}/{model}"
        return `${slashPrefix}models/${nextBrand}/${nextModel}`;
      } else {
        const nextBrand =
          landingToModelsJSON[brand2 as keyof typeof landingToModelsJSON];
        const nextModel =
          landingToModelsJSON[model2 as keyof typeof landingToModelsJSON];
        if (!nextBrand || !nextModel) {
          // No mapping found, return original match
          return _match;
        }

        // Matched "auto/marken-modelle/{brand}/{model}" without leading slash
        return `${nonSlashPrefix}models/${nextBrand}/${nextModel}`;
      }
    }
  );
};

// Upgrade internal finn.com links from http to https, and normalize the apex
// finn.com host to www.finn.com in the same pass. Cosmic editors occasionally
// paste `http://www.finn.com/...` or `http://finn.com/...` hrefs into page
// content; left as-is they trip Ahrefs' "HTTPS page has internal links to
// HTTP" audit and add a needless 308 redirect hop (http -> https, apex -> www).
// Scoped to finn.com hosts and matched only inside an href attribute, so
// external links and non-finn domains are never touched. The trailing
// lookahead anchors the host boundary: it requires a path/port/query/fragment
// or the closing quote right after `finn.com`, so look-alike hosts such as
// `finn.community` or `finn.com.evil.com` are NOT rewritten.
export const upgradeInternalLinksToHttps = (html: string) =>
  html?.replace(
    /(href=["'])http:\/\/(?:www\.)?finn\.com(?=[/:?#"'])/gi,
    '$1https://www.finn.com'
  );

export const applySEOAdjustments = (html: string) => {
  // Serve rich-text Cosmic images resized via imgix instead of as raw
  // multi-MB originals (applies to every dangerouslySetInnerHTML/parse path).
  // sanitizeCosmicHtml wraps the whole pipeline as the LAST step so it also
  // cleans anything the SEO rewrites emit, and runs after custom Cosmic tags
  // have already been extracted upstream (so it can't strip them).
  return sanitizeCosmicHtml(
    rewriteCosmicImagesInHtml(
      rewriteMarkenModelleLinks(
        replaceLandingRedirects(
          upgradeInternalLinksToHttps(
            /* TODO optimize this replaceAll
      likely with 
      
      replace(
        /<a\s+href="(?:https:\/\/www\.finn\.com)?\/de-DE(?:\/abo)?">Auto Abo(?: von FINN)?<\/a>/g,
        '<a href="/de-DE/auto-abo">Auto Abo</a>',
      )
      
      but it requires some testing and tests adding, so in not now
    */
            html
              ?.replaceAll(
                '<a href="https://www.finn.com/de-DE/abo">Auto Abo</a>',
                '<a href="https://www.finn.com/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="/de-DE/abo">Auto Abo</a>',
                '<a href="/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="https://www.finn.com/de-DE">Auto Abo</a>',
                '<a href="https://www.finn.com/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="/de-DE">Auto Abo</a>',
                '<a href="/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="/de-DE">Auto Abo von FINN</a>',
                '<a href="/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="https://www.finn.com/de-DE">Auto Abo von FINN</a>',
                '<a href="https://www.finn.com/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="/de-DE">Auto Abo von FINN.</a>',
                '<a href="/de-DE/auto-abo">Auto Abo</a>'
              )
              ?.replaceAll(
                '<a href="https://www.finn.com/de-DE">Auto Abo von FINN.</a>',
                '<a href="https://www.finn.com/de-DE/auto-abo">Auto Abo</a>'
              )
              // auto replace h tags that should not be h tag, just look like h tag
              ?.replace(
                /<h1p\b[^>]*>([\s\S]*?)<\/h1p>/gi,
                '<p class="!mobile-t1-semibold md:!web-t1-semibold before:hidden after:hidden">$1</p>'
              )
              ?.replace(
                /<h2p\b[^>]*>([\s\S]*?)<\/h2p>/gi,
                '<p class="!mobile-t2-semibold md:!web-t2-semibold before:hidden after:hidden">$1</p>'
              )
              ?.replace(
                /<h3p\b[^>]*>([\s\S]*?)<\/h3p>/gi,
                '<p class="!mobile-t3-semibold md:!web-t3-semibold before:hidden after:hidden">$1</p>'
              )
              ?.replace(
                /<h4p\b[^>]*>([\s\S]*?)<\/h4p>/gi,
                '<p class="!mobile-t4-semibold md:!web-t4-semibold before:hidden after:hidden">$1</p>'
              )
              // Make all links bold and underlined.
              // Exception: respect an explicit `!no-underline` opt-out (e.g. the
              // Autolink TOC, which draws its own underline via `border-b`). Forcing
              // `!underline` on top of that renders a second line — the double
              // underline bug. Such links still receive `!font-semibold`.
              ?.replace(/<a\b([^>]*)>/gi, (_match, attrs) => {
                // Check if class attribute exists
                const classMatch = attrs.match(/class=["']([^"']*)["']/i);
                const existingClasses = classMatch ? classMatch[1].trim() : '';
                const optsOutOfUnderline = /(^|\s)!no-underline(\s|$)/.test(
                  existingClasses
                );
                const addedClasses = optsOutOfUnderline
                  ? '!font-semibold'
                  : '!font-semibold !underline';

                if (classMatch) {
                  // Append to existing class attribute
                  const newAttrs = attrs.replace(
                    /class=["']([^"']*)["']/i,
                    `class="${existingClasses}${existingClasses ? ' ' : ''}${addedClasses}"`
                  );

                  return `<a${newAttrs}>`;
                } else {
                  // Add new class attribute
                  return `<a${attrs} class="${addedClasses}">`;
                }
              })
          )
        )
      )
    )
  );
};

// Parser method throw an error when the content not a string
// This function jus to catch the error to prevent the broken pages
export const parseToHtml = (
  htmlContent: string
): ReactNode | ReactNode[] | string => {
  try {
    return Parser(applySEOAdjustments(htmlContent)) as unknown as
      | ReactNode
      | ReactNode[];
  } catch {
    return '';
  }
};

export const getAnchorTarget = (href: string) => {
  return href.startsWith('http') ? '_blank' : '_self';
};

const SCROLL_TOP_OFFSET = 30;
export const scrollToTarget = (
  target: HTMLElement | null,
  topOffsetOverride?: number,
  behavior: ScrollBehavior = 'smooth'
) => {
  if (!target) return;
  const targetPosition = target.getBoundingClientRect().top;
  const offsetPosition =
    targetPosition + window.scrollY - (topOffsetOverride ?? SCROLL_TOP_OFFSET);

  if (behavior === 'auto') {
    const previousScrollBehavior =
      document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, offsetPosition);
    document.documentElement.style.scrollBehavior = previousScrollBehavior;

    return;
  }

  window.scrollTo({
    top: offsetPosition,
    behavior,
  });
};

export const scrollToTopWithoutAnimation = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
};

export const targetElementTopAboveViewport = (
  target: HTMLElement | null
): boolean | null => {
  if (!target) {
    return null;
  }
  const targetTop = target.getBoundingClientRect().top;
  if (targetTop < 0) {
    return true;
  }

  return false;
};

export const targetElementTopBeyondThreshold = (
  target: HTMLElement | null,
  thresholdPercentage = 50
): boolean | null => {
  if (!target) {
    return null;
  }
  const targetTop = target.getBoundingClientRect().top;
  const windowHeight = window.innerHeight;
  const targetTopPercentage = (targetTop * 100) / windowHeight;

  // if thresholdPercentage is 50, then targetTopPercentage needs to be
  // greater than 50 to be beyond the threshold which will trigger the auto scroll
  return targetTopPercentage > thresholdPercentage;
};

/**
 * Generates a slug-like ID from text content for heading elements.
 * Converts text to lowercase, keeps only letters and digits, and removes all types of listing prefixes.
 *
 * @param text - The text content to convert to an ID
 * @returns A clean ID string suitable for HTML id attributes
 *
 * @example
 * generateHeadingId("2. Audi A4 Avant") // returns "audia4avant"
 * generateHeadingId("a. First Option") // returns "firstoption"
 * generateHeadingId("i. Introduction") // returns "introduction"
 * generateHeadingId("BMW X5 Review") // returns "bmwx5review"
 */
export const generateHeadingId = (text: string): string => {
  // First, remove common listing patterns before cleaning
  const cleanText = text
    // Remove numeric prefixes: "1.", "1)", "123.", etc.
    .replace(/^\s*\d+[.)]\s*/, '')
    // Remove single letter prefixes: "a.", "a)", "A.", "A)", etc.
    .replace(/^\s*[a-zA-Z][.)]\s*/, '')
    // Remove roman numeral prefixes: "i.", "ii)", "IV.", "XII)", etc.
    .replace(/^\s*[ivxlcdmIVXLCDM]+[.)]\s*/, '')
    // Remove bullet points and dashes: "•", "-", "*", etc.
    .replace(/^\s*[•\-*]\s*/, '');

  // Then clean and format for ID
  return cleanText
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // keep only letters and digits
    .replace(/^[0-9]+/, ''); // remove any remaining leading digits
};

/**
 * Conditionally wraps children with a wrapper component.
 * Useful for avoiding JSX duplication when a wrapper is only needed in certain conditions.
 *
 * @example
 * ```tsx
 * <ConditionalWrapper
 *   condition={needsWrapper}
 *   wrapper={(children) => <SomeProvider>{children}</SomeProvider>}
 * >
 *   <Content />
 * </ConditionalWrapper>
 * ```
 */
export const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: ReactNode) => ReactNode;
  children: ReactNode;
}) => (condition ? wrapper(children) : children);
